import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationStart, Router } from '@angular/router';
import { filter, tap } from 'rxjs';

import { ConfirmDialogComponent } from '../../components/common/confirm-dialog/confirm.dialog';
import { CurrentVersion } from '../../models/current-version.model';
import { ApiService } from '../api.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { appVersionRefreshDialogId } from './constants';

@Injectable({ providedIn: 'root' })
export class AppVersionRefreshService {
  private readonly apiService = inject(ApiService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly dialog = inject(MatDialog);

  constructor() {
    inject(Router)
      .events.pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        this.checkCurrentVersion();
      });
  }

  initializeApplication() {
    return this.apiService.getCurrentVersion().pipe(
      tap((currentVersion: CurrentVersion) => {
        this.setNewAppVersion(currentVersion);
      }),
    );
  }

  checkCurrentVersion(): void {
    this.apiService
      .getCurrentVersion()
      .subscribe((currentVersion: CurrentVersion) => {
        const localBuildNumber = this.localStorageService.get('appVersion');

        if (currentVersion.buildNumber !== localBuildNumber) {
          this.openRefreshDialog(currentVersion);
        }
      });
  }

  openRefreshDialog(currentVersion: CurrentVersion): void {
    if (
      this.dialog.openDialogs.find(
        (dialog) => dialog.id === appVersionRefreshDialogId,
      )
    ) {
      return;
    }

    const resetDialog = this.dialog.open(ConfirmDialogComponent, {
      height: 'auto',
      width: '407px',
      id: appVersionRefreshDialogId,
      disableClose: true,
      data: {
        title: 'Software Update',
        message:
          'This upgrade contains new features and enhancements. The update will only take a second.',
        buttons: [
          {
            text: 'Update',
            buttonClass: 'popup-button',
            returnValue: true,
          },
        ],
      },
    });

    resetDialog.afterClosed().subscribe((res) => {
      if (res) {
        this.setNewAppVersion(currentVersion);
        this.apiService.reload();
      }
    });
  }

  setNewAppVersion(currentVersion: CurrentVersion) {
    this.localStorageService.set('lastUpdated', currentVersion.lastUpdated);
    this.localStorageService.set('appVersion', currentVersion.buildNumber);
  }
}
